import { useManageReportCustomInputs } from '@faceup/report'
import type { Attachment } from '@faceup/ui'
import {
  Institution,
  LONG_STRING_MAX_LENGTH,
  RecipientsOnReportForm,
  SHORT_STRING_MAX_LENGTH,
  STRING_MIN_LENGTH,
} from '@faceup/utils'
import { useCallback, useContext, useMemo, useState } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../../../__generated__'
import { ReportFormContext, ReportInputType } from '../../../Contexts/ReportFormContext'
import { UserContext } from '../../../Contexts/UserContext'
import { useRecipients } from './useRecipients'

const fragments = {
  useManageCreateReportForm_publicReportSource: graphql(`
    fragment useManageCreateReportForm_publicReportSource on PublicReportSource {
      id
      isDefault
      config {
        ... on FormReportSourceConfiguration {
          id
          recipientsOnReportForm
        }
      }

      organizationalUnits {
        id
        organizationalUnitName
      }
    }
  `),
  useManageCreateReportForm_publicCompany: graphql(`
    fragment useManageCreateReportForm_publicCompany on PublicCompany {
      id
      organizationalUnitName
    }
  `),
}

type UseManageCreateReportFormProps = {
  institution: FragmentType<typeof fragments.useManageCreateReportForm_publicCompany> | null
  reportSource: FragmentType<typeof fragments.useManageCreateReportForm_publicReportSource> | null
}

export const useManageCreateReportForm = ({
  institution: _institution,
  reportSource: _reportSource,
}: UseManageCreateReportFormProps) => {
  const institution = getFragmentData(
    fragments.useManageCreateReportForm_publicCompany,
    _institution
  )
  const reportSource = getFragmentData(
    fragments.useManageCreateReportForm_publicReportSource,
    _reportSource
  )
  const {
    recording,
    setRecording,
    buildRecordingFile,
    isAnonymous,
    setIsAnonymous,
    reportInputType,
    setReportInputType,
  } = useContext(ReportFormContext)

  const { reportSource: institutionFromContext } = useContext(UserContext)

  const [attachments, setAttachments] = useState<Attachment[]>([])

  const valueWithError = useCallback(
    <T>(v: { value: T; error: boolean }) => ({ ...v, error: true }),
    []
  )

  const [categoryInput, setCategoryInput] = useState<{
    value: string | undefined
    error: boolean
  }>({
    value: undefined,
    error: false,
  })

  const [nameInput, setNameInput] = useState({
    value: '',
    error: false,
  })

  const [moreInfoInput, setMoreInfoInput] = useState({
    value: '',
    error: false,
  })

  const [senderNameInput, setSenderNameInput] = useState({
    value: '',
    error: false,
  })

  const [attachmentsEnabled, setAttachmentsEnabled] = useState({
    value: true,
    error: false,
  })

  const [recipientsOnReportForm, setRecipientsOnReportForm] = useState<{
    value: RecipientsOnReportForm
    error: boolean
  }>({
    value: RecipientsOnReportForm.Visible,
    error: false,
  })

  const selectableOrganizationUnits = useMemo(
    () => reportSource?.organizationalUnits ?? [],
    [reportSource?.organizationalUnits]
  )

  const [organizationUnitInput, setOrganizationUnitInput] = useState({
    value: selectableOrganizationUnits.length > 1 ? '' : selectableOrganizationUnits?.[0]?.id,
    error: false,
  })

  const selectedOrganizationalUnit = useMemo(
    () =>
      selectableOrganizationUnits.find(
        selectable => selectable?.id === organizationUnitInput.value
      ) ?? institution,
    [institution, organizationUnitInput.value, selectableOrganizationUnits]
  )

  const isSchoolDefaultForm =
    institutionFromContext?.type === Institution.School && reportSource?.isDefault

  const { recipients, changeRecipient, areRecipientsLoading } = useRecipients(
    selectedOrganizationalUnit?.id,
    categoryInput.value,
    reportSource?.id,
    recipientsOnReportForm.value
  )

  const {
    customFormItems,
    setCustomFormItems,
    validateCustomFormItems,
    resetCustomFormItems,
    getValuesForMutation,
  } = useManageReportCustomInputs(categoryInput.value ?? '', reportSource?.id ?? null)

  const clearForm = useCallback(() => {
    setNameInput({ value: '', error: false })
    setMoreInfoInput({ value: '', error: false })
    setAttachments([])
    setRecording(null)
    setCategoryInput({ value: undefined, error: false })
    setSenderNameInput({ value: '', error: false })
    resetCustomFormItems()
  }, [setRecording, resetCustomFormItems])

  const resetVoiceFieldsErrors = useCallback(() => {
    setMoreInfoInput(prev => ({ ...prev, error: false }))
    // todo(ondra): add sender name reset in following PR
    // setSenderNameInput(prev => ({ ...prev, error: false }))
    // todo(ondra): add custom fields reset in following PR
    // resetCustomFormItems()
  }, [])

  const validateForm = useCallback(async () => {
    const result = {
      isFormValid: true,
      hasBothTextAndVoice: false,
    }
    if (!categoryInput.value) {
      setCategoryInput(valueWithError)

      result.isFormValid = false
    }

    if (
      reportInputType === ReportInputType.Text &&
      (moreInfoInput.value.length < STRING_MIN_LENGTH ||
        moreInfoInput.value.length > LONG_STRING_MAX_LENGTH) &&
      !isSchoolDefaultForm
    ) {
      setMoreInfoInput(valueWithError)
      result.isFormValid = false
    }
    if (reportInputType === ReportInputType.Voice && !recording) {
      setMoreInfoInput(valueWithError)
      result.isFormValid = false
    }
    if (moreInfoInput.value && recording) {
      result.hasBothTextAndVoice = true
    }

    if (
      !isAnonymous &&
      (senderNameInput.value.length < STRING_MIN_LENGTH ||
        senderNameInput.value.length > SHORT_STRING_MAX_LENGTH) &&
      !isSchoolDefaultForm
    ) {
      setSenderNameInput(valueWithError)

      result.isFormValid = false
    }

    if (
      (nameInput.value.trim().length < STRING_MIN_LENGTH ||
        !nameInput.value.trim().includes(' ') ||
        nameInput.value.trim().length > SHORT_STRING_MAX_LENGTH) &&
      isSchoolDefaultForm
    ) {
      setNameInput(valueWithError)

      result.isFormValid = false
    }

    if (selectableOrganizationUnits.length > 1 && !organizationUnitInput.value) {
      setOrganizationUnitInput(valueWithError)

      result.isFormValid = false
    }

    if (!(await validateCustomFormItems())) {
      result.isFormValid = false
    }

    return result
  }, [
    categoryInput.value,
    recording,
    moreInfoInput.value,
    isSchoolDefaultForm,
    isAnonymous,
    senderNameInput.value,
    nameInput.value,
    selectableOrganizationUnits.length,
    organizationUnitInput.value,
    valueWithError,
    validateCustomFormItems,
    reportInputType,
  ])

  return {
    categoryInput,
    setCategoryInput,
    moreInfoInput,
    setMoreInfoInput,
    senderNameInput,
    setSenderNameInput,
    nameInput,
    setNameInput,
    attachments,
    setAttachments,
    customFormItems,
    setCustomFormItems,
    getValuesForMutation,
    clearForm,
    resetVoiceFieldsErrors,
    recording,
    setRecording,
    buildRecordingFile,
    validateForm,
    organizationUnitInput,
    setOrganizationUnitInput,
    selectedOrganizationalUnit,
    selectableOrganizationUnits,
    isAnonymous,
    setIsAnonymous,
    attachmentsEnabled,
    setAttachmentsEnabled,
    recipientsOnReportForm,
    setRecipientsOnReportForm,
    recipients,
    areRecipientsLoading,
    changeRecipient,
    isSchoolDefaultForm,
    reportInputType,
    setReportInputType,
  }
}
